import React, { Fragment, useState, useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { debounce } from "lodash";
import { GoogleMap, Autocomplete, Marker, useJsApiLoader } from "@react-google-maps/api";
import { FormControlLabel, Checkbox, Grid, Box, Avatar, Typography, MenuItem, Select, OutlinedInput, Radio, RadioGroup } from "@mui/material";
import SoftInput from "components/SoftInput";
import DefaultLayout from "layouts/authentication/components/DashboardDefaultLayout/DefaultLayout";
import SoftBox from "components/SoftBox";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ClearIcon from "@mui/icons-material/Clear";
import SoftButton from "components/SoftButton";
import { useNavigate } from "react-router-dom";
import TodayIcon from "@mui/icons-material/Today";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import EventIcon from "@mui/icons-material/Event";
import { useGlobalState } from "globalState/globalState";
import { fetchWeatherData } from "./weatherUtils";
import DeviceThermostatIcon from "@mui/icons-material/DeviceThermostat";
import AirIcon from "@mui/icons-material/Air";
import { GiWindsock } from "react-icons/gi";
import WeatherConditionsCard from "./components/WeatherConditionsCard";
import Switch from "@mui/material/Switch";
import { Link } from "react-router-dom";
import SoftTypography from "components/SoftTypography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import App_logo from "assets/images/BMAx_orderSystem.png";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { styled, alpha } from "@mui/material/styles";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import "dayjs/locale/nb";
import { getGoogleMapsApiKey } from "utils/appKeys";
import { getBmaxSettings } from "api/apiService";


const containerStyle = {
  width: "100%",
  height: "300px",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px",
};

const initialCenter = {
  lat: -3.745,
  lng: -38.523,
};

const BluePickersDay = styled(PickersDay)(({ theme }) => ({
  // Base hover
  "&:hover": {
    backgroundColor: alpha(theme.palette.info.main, 0.12),
  },

  // TODAY (not selected)
  '&[aria-current="date"]': {
    border: `2px solid ${theme.palette.info.main}`,
    backgroundColor: alpha(theme.palette.info.main, 0.10),
    color: theme.palette.info.main,
  },

  // TODAY + SELECTED
  '&[aria-current="date"].Mui-selected, \
   &[aria-current="date"].Mui-selected:hover, \
   &[aria-current="date"].Mui-selected.Mui-focusVisible': {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.common.white,
    borderColor: theme.palette.info.main,
  },

   // selected (cover all interaction states)
  "&.Mui-selected, \
   &.Mui-selected:hover, \
   &.Mui-selected.Mui-focusVisible, \
   &.Mui-selected:focus, \
   &.Mui-selected:active": {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.common.white,
    border: `2px solid ${theme.palette.info.main}`, // keeps blue ring if today
  },
}));

const GOOGLE_MAP_API_KEY = getGoogleMapsApiKey();


const SearchAddress = () => {
  const location = useLocation();
  const { state } = useGlobalState();
  const [autocomplete, setAutocomplete] = useState(null);
  const [address, setAddress] = useState(location.state?.address || "");
  const [center, setCenter] = useState(initialCenter);
  const [markerPosition, setMarkerPosition] = useState(initialCenter);
  const [showMap, setShowMap] = useState(location.state?.showMap ?? false);
  const [locationSelected, setLocationSelected] = useState(false);
  const [showDateTime, setShowDateTime] = useState(location.state?.showDateTime ?? false);
  // "later" | "asap" | "chosen". Utledet fra showDateTime for ordre som ble
  // startet før de tre valgene fantes.
  const [deliveryTiming, setDeliveryTiming] = useState(
    location.state?.deliveryTiming ?? (location.state?.showDateTime ? "chosen" : "later")
  );
  // Ventetidsmeldingen er redigerbar i Innstillinger, fordi tallet endrer seg
  // med sesongen. Tom streng = ingen melding.
  const [leadTimeNote, setLeadTimeNote] = useState("");
  const [weatherData, setWeatherData] = useState(location.state?.weatherData || []);
  const [isLoading, setIsLoading] = useState(false);
  const [dateErrors, setDateErrors] = useState({}); // Store errors for each index
  const [timeErrors, setTimeErrors] = useState({}); // Store errors for each index
  // Hvilken rad sin tidsvelger som står åpen — én om gangen, identifisert med
  // indeks. Dette var tidligere én delt boolean: å klikke på ÉN tidsvelger satte
  // den til true, som åpnet menyen i ALLE radene samtidig. Menyene legger seg
  // oppå hverandre, så man traff alltid den siste — og derfor endret bare siste
  // rad tidspunkt uansett hvilken rad man klikket på.
  const [openTimeIndex, setOpenTimeIndex] = useState(null);



  // Get today's date
  const getNextAvailableDate = () => {
    let date = new Date();
    let day = date.getDay(); // 0 = Sunday, 6 = Saturday

    // If today is Saturday (6) → Move to Monday (8th - 6 = 2)
    // If today is Sunday (0) → Move to Monday (1st day)
    if (day === 6) {
        date.setDate(date.getDate() + 2); // Move to Monday
    } else if (day === 0) {
        date.setDate(date.getDate() + 1); // Move to Monday
    }

    return date.toISOString().split("T")[0]; // Format YYYY-MM-DD
  };
  
  const todayDate = getNextAvailableDate(); // Automatically set a valid date
  const [dateTimeFields, setDateTimeFields] = useState(
      location.state?.dateTimeFields?.length
        ? location.state.dateTimeFields
        : [{ date: todayDate, time: "" }]
  );

  const navigate = useNavigate();
  const { dispatch } = useGlobalState();

  console.log("dateTimeFields:", dateTimeFields); // Debugging
  console.log("weatherData:", weatherData); // Debugging

  const debouncedUpdateWeather = useCallback(
    debounce((lat, lng, dateTimeKey) => {
        updateWeatherData(lat, lng, dateTimeKey);
    }, 1000),
    []
  );

 

  const libraries = useMemo(() => ["places", "geometry", "drawing"], []);
  const loaderOptions = useMemo(
    () => ({
      googleMapsApiKey: GOOGLE_MAP_API_KEY,
      libraries,
    }),
    [libraries]
  );

  const { isLoaded } = useJsApiLoader(loaderOptions);
  useEffect(() => {
      if (isLoaded) {
        setIsLoading(false);  // Hide loader when Google Maps API is loaded
      }
    }, [isLoaded]);

  

  


  // Ensure when an address is selected, showMap is updated
 
  useEffect(() => {
    if (
      isLoaded && 
      address &&
      dateTimeFields.length > 0 &&
      dateTimeFields.every(field => field.date && field.time) 
    ) {
      console.log("Valid dateTimeFields:", dateTimeFields);

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: address }, async (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          const newLat = location.lat();
          const newLng = location.lng();

          setCenter({ lat: newLat, lng: newLng });
          setMarkerPosition({ lat: newLat, lng: newLng });
          setShowMap(true);
          setLocationSelected(true);

          // Ensure that weather is updated for each date-time field separately
          dateTimeFields.forEach(field => {
              if (field.date && field.time) {
                  const dateTimeKey = `${field.date}_${field.time}`;
                  debouncedUpdateWeather(newLat, newLng, dateTimeKey);
              }
          });
        }
      });
    }
  }, [isLoaded, address, dateTimeFields]);


  const updateWeatherData = async (lat, lng, dateTimeKey) => {
    if (!lat || !lng || !dateTimeKey) return; // Prevent unnecessary calls

    setIsLoading(true);
    try {
      const weatherTimeseries = await fetchWeatherData(lat, lng);
      console.log("Weather API Response:", weatherTimeseries);

      const timeInterval = [0, 6, 18]; // Define allowed time intervals

      // Find the field matching the dateTimeKey
      const fieldToUpdate = dateTimeFields.find(field => `${field.date}_${field.time}` === dateTimeKey);
      if (!fieldToUpdate) {
          console.warn("No matching field found for dateTimeKey:", dateTimeKey);
          setIsLoading(false);
          return;
      }

      // Correctly determine the closest valid interval
      const timeFormat = getCloseInterval(fieldToUpdate.time, timeInterval);
      
      // Ensure expected format: YYYY-MM-DDTHH:00:00Z
      const selectedDateTime = `${fieldToUpdate.date}T${timeFormat}`;
     
      // Find matching weather entry
      const weather = weatherTimeseries.find((item) => {
          return item.time.startsWith(selectedDateTime); // Handle potential seconds difference
      });

      if (!weather) {
          console.warn("No weather data found for:", selectedDateTime);
          setIsLoading(false);
          return;
      }

      // Construct updated weather data entry
      const updatedWeatherEntry = {
        date: fieldToUpdate.date,
        time: fieldToUpdate.time,
        weather: {
          temperature: weather.data.instant.details.air_temperature,
          air_pressure: weather.data.instant.details.air_pressure_at_sea_level,
          cloud_area_fraction: weather.data.instant.details.cloud_area_fraction,
          relative_humidity: weather.data.instant.details.relative_humidity,
          wind_direction: weather.data.instant.details.wind_from_direction,
          wind_speed: weather.data.instant.details.wind_speed,
          icon:
            weather.data.next_1_hours?.summary.symbol_code ||
            weather.data.next_6_hours?.summary.symbol_code ||
            weather.data.next_12_hours?.summary.symbol_code ||
            "default_icon",
        },
      };

      // Update weatherData state
      setWeatherData((prevWeatherData) => {
        const existingIndex = prevWeatherData.findIndex(
          (item) => item.date === fieldToUpdate.date && item.time === fieldToUpdate.time
        );

        if (existingIndex !== -1) {
          console.log("Updating existing weather entry...");
          const updatedWeatherData = [...prevWeatherData];
          updatedWeatherData[existingIndex] = updatedWeatherEntry;
          return updatedWeatherData;
        } else {
          console.log("Adding new weather entry...");
          return [...prevWeatherData, updatedWeatherEntry];
        }
      });

    } catch (error) {
        console.error("Error fetching or processing weather data:", error);
    } finally {
        setIsLoading(false);
    }
  };


  // ✅ FIXED FUNCTION TO FIND THE CLOSEST INTERVAL
  const getCloseInterval = function (time, intervalArray) {

    const hour = parseInt(time.split(":")[0], 10);
    
    let closestInterval = intervalArray[0];
    let minDifference = Math.abs(hour - closestInterval);

    for (let i = 1; i < intervalArray.length; i++) {
      let difference = Math.abs(hour - intervalArray[i]);
      if (difference < minDifference) {
        minDifference = difference;
        closestInterval = intervalArray[i];
      }
    }
    return closestInterval.toString().padStart(2, "0") + ":00:00Z";
  };


  const handleLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };


  useEffect(() => {
    let alive = true;
    getBmaxSettings()
      .then((res) => {
        if (alive) setLeadTimeNote(res?.data?.extra?.deliveryLeadTimeNote || "");
      })
      .catch(() => {
        // Meldingen er hyggelig å ha, ikke nødvendig. Feiler kallet, viser vi
        // ingen melding i stedet for å stoppe bestillingen.
      });
    return () => {
      alive = false;
    };
  }, []);

  const handleNextClick = () => {
    // «Jeg ønsker en bestemt dag» krever dag OG tidsintervall. Uten sjekken gikk
    // bestillingen videre med en tom tid, og produsenten så «tid ikke satt».
    if (deliveryTiming === "chosen") {
      const missing = {};
      dateTimeFields.forEach((f, i) => {
        if (!f.time) missing[i] = "Velg ønsket tidspunkt.";
      });
      if (Object.keys(missing).length) {
        setTimeErrors((prev) => ({ ...prev, ...missing }));
        return;
      }
    }

    const step1Data = {
      address,
      // Bare «bestemt dag» har en dato. Ved «Så snart som mulig» og «Avtal
      // senere» lå standardraden med DAGENS dato igjen i state og ble lagret på
      // ordren — produsenten så dagens dato som kundens ønske.
      dateTimeFields: deliveryTiming === "chosen" ? dateTimeFields : [],
      deliveryTiming,
      weatherData,
      showMap,
      showDateTime,
      totalArea: location.state?.totalArea || 0, // Preserve data if exists
      personName: location.state?.personName || "",
      castWithDrop: location.state?.castWithDrop || false,
      delay: location.state?.delay || false,
      steelFiber: location.state?.steelFiber || false,
      accelerator: location.state?.accelerator || false,
      uploadingTypeChecked: location.state?.uploadingTypeChecked || false,
      selectedItem: location.state?.selectedItem || null,
      vehicleType: location.state?.vehicleType || "",
      selectedProjectType: location.state?.selectedProjectType || "",
      showAdditionalOptions: location.state?.showAdditionalOptions || false,
      addAmountChecked: location.state?.addAmountChecked || false,
      pumpMixerWashChecked: location.state?.pumpMixerWashChecked || false,
      addHoseChecked: location.state?.addHoseChecked || false,
      hoseAmount: location.state?.hoseAmount || 0,
      concreteConfig: location.state?.concreteConfig || null,
      concreteConfigData: location.state?.concreteConfigData || null,
      displayValue: location.state?.displayValue || "",
      offerOnCasting: location.state?.offerOnCasting || false,
      offerOnFloorSanding: location.state?.offerOnFloorSanding || false,
      responsibility: location.state?.responsibility || "selfBuilder",
      acceptedTerms: location.state?.acceptedTerms || false,
      uploadedFiles: location.state?.uploadedFiles || [],
      fileIDs: location.state?.fileIDs || [],
      selectedFile: location.state?.selectedFile || null,
      vendorData: location.state?.vendorData || null,
      projectDescription: location.state?.projectDescription || "",
      orderFor: location.state?.orderFor || "",
    };

    console.log("Navigating to area-calculate with:", step1Data);
    
    navigate("/area-calculate", { state: step1Data });
  }; 

  

  // const handlePlaceChanged = () => {
  //   if (autocomplete) {
  //     const place = autocomplete.getPlace();
  //     if (place.geometry) {
  //       const location = place.geometry.location;
  //       const newLat = location.lat();
  //       const newLng = location.lng();

  //       setAddress(place.formatted_address);
  //       setCenter({ lat: newLat, lng: newLng });
  //       setMarkerPosition({ lat: newLat, lng: newLng });
  //       setShowMap(true);
  //       setLocationSelected(true);
  //     }
  //   }
  // };

  const handlePlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const location = place.geometry.location;
        const newLat = location.lat();
        const newLng = location.lng();

        // Custom address formatter
        const components = {};
        place.address_components?.forEach(component => {
          component.types.forEach(type => {
            components[type] = component.long_name;
          });
        });

        const road = components.route || '';
        const number = components.street_number || '';
        const postcode = components.postal_code || '';
        const town =
          components.postal_town ||
          components.locality ||
          components.sublocality ||
          components.administrative_area_level_2 ||
          components.administrative_area_level_1 ||
          '';

        const formatted = `${road} ${number}, ${postcode} ${town}`.trim();

        // Apply formatted address
        setAddress(formatted);
        setCenter({ lat: newLat, lng: newLng });
        setMarkerPosition({ lat: newLat, lng: newLng });
        setShowMap(true);
        setLocationSelected(true);
      }
    }
  };


  const handleClear = () => {
    setAddress("");
    setCenter(initialCenter);
    setMarkerPosition(initialCenter);
    setShowMap(false);
    setLocationSelected(false);
    setShowDateTime(false);
    setDateTimeFields([{ date: "", time: "" }]);
    setWeatherData([]);
  };

  // Faste 2-timers leveringsvinduer. Kunden valgte tidligere ett klokkeslett,
  // som lovet en presisjon ingen betongbil kan holde — vinduene sier det samme
  // som «Endelig leveringstid bekreftes av leverandør» gjør i teksten over.
  //
  // Lagres som starttidspunktet ("08:00"), ikke som "08.00 - 10.00": alt
  // nedstrøms — validering, værdata, produsentsiden, sortering — parser
  // HH:MM. Vinduet er en visning, ikke et nytt dataformat.
  const TIME_SLOTS = ["08:00", "10:00", "12:00", "14:00"];

  const slotLabel = (start) => {
    if (!start) return "";
    const [h, m] = String(start).split(":").map(Number);
    if (!Number.isFinite(h)) return start;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(h)}.${pad(m || 0)} - ${pad(h + 2)}.${pad(m || 0)}`;
  };

  const handleTimingChange = (value) => {
    setDeliveryTiming(value);
    // showDateTime følger med videre: resten av veiviseren og Summary leser
    // fortsatt det flagget.
    setShowDateTime(value === "chosen");
    // Uten dette ble tidspunkter man hadde fylt ut stående igjen i state og
    // sendt med bestillingen selv om kunden byttet til «avtal senere».
    if (value !== "chosen") {
      setDateErrors({});
      setTimeErrors({});
      setWeatherData([]);
    }
  };

  const handleCheckboxChange = (event) => {
    setShowDateTime(event.target.checked);
  };

  const handleAddDateTimeField = () => {
    setDateTimeFields([...dateTimeFields, { date: todayDate, time: "" }]);
  };

  // const getCloseInterval = function (time, intervalArray) {
  //   let index = 0;
  //   console.log("getCloseInterval time:", time);
  //   console.log("getCloseInterval intervalArray:", intervalArray);
  //   const hour = +time.split(":")[0];

  //   console.log("h:", hour);

  //   //02:23 [0,6,18]                       0<2 && 6>2

  //   index = intervalArray.find((interval, index, array) => {
  //     console.log("last:", array.length === index + 1 ? 24 : index + 1);

  //     if (array.length === index + 1) {
  //       //last index
  //       return index;
  //     }

  //     return interval <= hour && array[index + 1] > hour;
  //   });

  //   return index.toString().padStart(2, "0") + ":00:00Z";
  // };

  /**
   * Rene validatorer: de returnerer en feilmelding og rører ingen state.
   *
   * Tidligere satte valideringen BÅDE feilmeldingen og `dateTimeFields` — fra en
   * kopi av arrayet slik det så ut før endringen. To setState-kall kappet dermed
   * om den samme raden, og den siste vant. Nå avgjør regelen bare hva som er
   * galt; hvem som skriver det til state er ett sted.
   */
  const dateErrorFor = (selectedDate) => {
    if (!selectedDate) return "";
    if (selectedDate < currentDate || selectedDate > formattedMaxDate) {
      return `Vennligst velg en dato mellom ${currentDate} og ${formattedMaxDate}.`;
    }
    const day = new Date(selectedDate).getDay();
    if (day === 0 || day === 6) return "Helger (lørdag og søndag) er ikke tillatt.";
    return "";
  };

  const timeErrorFor = (selectedTime, selectedDate) => {
    if (!selectedTime) return "";
    const [hour, minutes] = selectedTime.split(":").map(Number);
    if (hour < 6 || hour > 15) return "Tid må være mellom 06:00 og 15:00";
    if (minutes !== 0) return "Tid må være i hele timer (f.eks. 08:00, 09:00, osv.).";

    const now = new Date();
    if (selectedDate === now.toISOString().split("T")[0]) {
      const nowString = `${String(now.getHours()).padStart(2, "0")}:${String(
        now.getMinutes()
      ).padStart(2, "0")}`;
      if (`${String(hour).padStart(2, "0")}:00` <= nowString) {
        return "Du kan ikke velge en tidligere tid for i dag.";
      }
    }
    return "";
  };

  const handleDateTimeChange = (index, field, value) => {
    const row = dateTimeFields[index];
    if (!row) return;

    const error = field === "date" ? dateErrorFor(value) : timeErrorFor(value, row.date);

    // Raden byttes ut, ikke muteres. `[...dateTimeFields]` kopierer bare arrayet —
    // objektene i det er de samme, så `kopi[index][field] = value` skrev rett inn
    // i den raden React allerede holder som gjeldende state.
    const next = { ...row, [field]: value };
    // En avvist verdi blir ikke stående. Er datoen avvist, tar den tiden med seg:
    // et tidspunkt på en dato som ikke er lov er ikke noe svar.
    if (error) next.time = "";

    setDateTimeFields((prev) => prev.map((r, i) => (i === index ? next : r)));

    if (field === "date") {
      setDateErrors((prev) => ({ ...prev, [index]: error }));
      // Tiden er nullstilt sammen med datoen, så en gammel tidsfeil hører ikke
      // lenger til noe felt.
      if (error) setTimeErrors((prev) => ({ ...prev, [index]: "" }));
    } else {
      setTimeErrors((prev) => ({ ...prev, [index]: error }));
    }

    if (next.date && next.time) {
      updateWeatherData(center.lat, center.lng, `${next.date}_${next.time}`);
    }
  };



  

  const handleClearDateTimeField = (index) => {
    const newDateTimeFields = dateTimeFields.filter((_, i) => i !== index);
    setDateTimeFields(newDateTimeFields);
    const newWeatherData = weatherData.filter((_, i) => i !== index);
    setWeatherData(newWeatherData);
  };

  const onMapLoad = useCallback((map) => {
    // fitBounds rundt ett punkt zoomer helt inn (nivå 22). Der har Google
    // ingen satellittbilder, fliser svarer 503 og kartet viser «map error».
    map.setZoom(18);
  }, []);

  useEffect(() => {
    if (autocomplete && address) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          setCenter({ lat: location.lat(), lng: location.lng() });
          setMarkerPosition({ lat: location.lat(), lng: location.lng() });
          setShowMap(true);
          setLocationSelected(true);
        }
      });
    }
  }, [autocomplete, address]);

  const handleBoxClick = () => {
    handleCheckboxChange({ target: { name: "addPumpWaste", checked: !showDateTime } });
  };

  const handleCheckboxClick = (event) => {
    event.stopPropagation();
    handleCheckboxChange(event);
  };

  

 const extraContent = (
    <Box
      sx={{
        // backgroundColor: "#006bb7",
        padding: "20px",
        marginTop: "90px",
      }}
    >
    
      
      <Grid container direction="column" gap={2}>
        <Typography color={"#fff"} variant="body2">
          <span style={{ fontWeight: "900", fontSize: "20px" }}>Steg 1</span> Velg adresse
        </Typography>

        <Box
          p={2}
          sx={{
            border: "1px solid #fff",
            width: "100%%",
            // backgroundColor: "#0b97ef",
            borderRadius: "10px",
          }}
        >
          <Typography color="#fff" variant="body2">
            <span style={{ fontWeight: "800" }}>Lever til:</span> {address}
          </Typography>
          {weatherData &&
            dateTimeFields &&
            dateTimeFields.map((field, index) => {
              if (!field.date || !field.time) {
                return null;
              }

              const weatherForDate = weatherData[index]?.weather || {};
             

              if (!weatherForDate || Object.keys(weatherForDate).length === 0) {
                return (
                  <Typography key={index} color="#fff" variant="body2" mt={2}>
                    Værdata er ikke tilgjengelig for {field.date}, {field.time}
                  </Typography>
                );
              }

              const customDateFormat = `${field.date.split("-")[2]}.${field.date.split("-")[1]}.${field.date.split("-")[0]}`;
              
 
              return (
                <WeatherConditionsCard
                  key={index}
                  index={index}
                  date={customDateFormat}
                  time={field.time}
                  weatherForDate={weatherForDate}
                />
              );
            })}
        </Box>
      </Grid>
      

    </Box>
  );


  // Get the current date
  const currentDate = new Date().toISOString().split("T")[0];

  // Calculate the max date (6 month from the current date)
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 180);
  const formattedMaxDate = maxDate.toISOString().split("T")[0];

  return isLoaded ? (
      
    <DefaultLayout extraContent={extraContent}>
      <Grid container >
        <Grid item xs={12} md={11} xl={11} mx="auto" mt={4} mb={1}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: locationSelected ? "flex-start" : "center",
              flexDirection: "column",
              width: "100%",
            }}
            mb={3}
          >
          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
              }}
            >
              <CircularProgress size={60} color="info" />
            </Box>
          )}
            <Grid container justifyContent="center">
              
              
              <Grid container justifyContent="center">
                <Grid item xs={12} mt={1}>
                  <SoftBox textAlign="center">
                    <SoftTypography variant="h2" sx={{ fontWeight: 'bolder', lineHeight: '1.2', color: '#006bb7' }}>
                      HVOR
                    </SoftTypography>
                  </SoftBox>
                </Grid>
                <Grid item xs={12}>
                  <SoftBox textAlign="center">
                    <SoftTypography variant="h2" sx={{ fontWeight: 'bolder', lineHeight: '1.2', color: '#000', display: 'inline' }}>
                      skal betong leveres
                    </SoftTypography>
                    <SoftTypography variant="h2" sx={{ fontWeight: 'bolder', lineHeight: '1.2', color: '#006bb7', display: 'inline' }}>
                      ?
                    </SoftTypography>
                  </SoftBox>
                </Grid>
              </Grid>
              <Grid item xs={12} mt={3}>
                <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
                  <SoftInput
                    placeholder="Søk etter sted.."
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    size="large"
                    icon={{
                      component: !address ? (
                        "search"
                      ) : (
                        <Box sx={{ cursor: "pointer" }}>
                          {address && <ClearIcon onClick={() => setAddress("")} />}
                        </Box>
                      ),
                      direction: "right",
                    }}
                    sx={{ width: "100%" }}
                  />
                </Autocomplete>
              </Grid>
            </Grid>
          </Box>

          {showMap && (
            <Box mt={3}>
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={18}
                onLoad={onMapLoad}
              >
                <Marker position={markerPosition} />
              </GoogleMap>
              {/* «Ønsket levering» — kundens skisse 11.09.2026. Tre valg på én
                  rad, og ved «bestemt dag» en dato pluss faste 2-timers vinduer.
                  Vinduene er nye: kunden valgte tidligere ett klokkeslett, som
                  lovet en presisjon ingen betongbil kan holde. */}
              <SoftBox
                mt={3}
                sx={{
                  background: "#fff",
                  border: "1px solid #e6ecf3",
                  borderRadius: "12px",
                  p: { xs: 2, sm: 3 },
                }}
              >
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", mb: 2.5 }}>
                  <LocalShippingIcon sx={{ fontSize: 34, color: "#1d7ff0", flex: "0 0 auto" }} />
                  <Box>
                    <Typography
                      sx={{ fontSize: "1.5rem", fontWeight: 800, color: "#0f1b2a", lineHeight: 1.15 }}
                    >
                      Ønsket levering
                    </Typography>
                    <Typography sx={{ fontSize: "0.9rem", color: "#67748e", mt: 0.25 }}>
                      Velg når du ønsker betongen levert. Endelig leveringstid bekreftes av
                      leverandør.
                    </Typography>
                  </Box>
                </Box>

                {/* Radene bygges for hånd: Soft UI-temaet setter column på
                    FormControlLabel, så knappen havnet over teksten. */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 1, sm: 3 }, mb: 2.5 }}>
                  {[
                    ["asap", "Så snart som mulig"],
                    ["later", "Avtal senere"],
                    ["chosen", "Jeg ønsker en bestemt dag"],
                  ].map(([value, label]) => {
                    const picked = deliveryTiming === value;
                    return (
                      <Box
                        key={value}
                        role="radio"
                        aria-checked={picked}
                        tabIndex={0}
                        onClick={() => handleTimingChange(value)}
                        onKeyDown={(e) => {
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            handleTimingChange(value);
                          }
                        }}
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}
                      >
                        <Radio
                          checked={picked}
                          readOnly
                          tabIndex={-1}
                          sx={{ p: 0.5, color: "#8a94a6", "&.Mui-checked": { color: "#1d7ff0" } }}
                        />
                        <Typography sx={{ fontSize: "1rem", color: "#0f1b2a" }}>{label}</Typography>
                      </Box>
                    );
                  })}
                </Box>

                {deliveryTiming === "chosen" && (
                  <Box>
                    {dateTimeFields.map((field, index) => (
                      <Fragment key={index}>
                        {index > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mt: 3,
                              mb: 1,
                              pt: 2,
                              borderTop: "1px solid #e6ecf3",
                            }}
                          >
                            <Typography sx={{ fontSize: "0.8rem", color: "#67748e" }}>
                              Alternativ {index + 1}
                            </Typography>
                            <SoftButton
                              size="small"
                              variant="text"
                              color="error"
                              onClick={() => handleClearDateTimeField(index)}
                            >
                              Fjern
                            </SoftButton>
                          </Box>
                        )}

                        <Typography
                          sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#0f1b2a", mb: 0.75 }}
                        >
                          Dato
                        </Typography>
                        {index === 0 && !!leadTimeNote && (
                          <Typography sx={{ color: "#c2410c", fontSize: "0.85rem", mb: 0.75 }}>
                            {leadTimeNote}
                          </Typography>
                        )}
                        <Box sx={{ maxWidth: 420 }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                            <DatePicker
                              format="dddd D. MMMM YYYY"
                              value={field.date ? dayjs(field.date) : null}
                              onChange={(v) =>
                                v && handleDateTimeChange(index, "date", v.format("YYYY-MM-DD"))
                              }
                              minDate={dayjs()}
                              maxDate={dayjs().add(180, "day")}
                              shouldDisableDate={(d) => [0, 6].includes(d.day())}
                              slots={{ day: BluePickersDay, openPickerIcon: KeyboardArrowDownIcon }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  error: !!dateErrors[index],
                                  helperText: dateErrors[index],
                                  InputProps: {
                                    startAdornment: (
                                      <CalendarMonthIcon sx={{ color: "#1d7ff0", mr: 1 }} />
                                    ),
                                  },
                                  sx: {
                                    "& .MuiInputBase-root": { height: 52, borderRadius: "10px" },
                                    "& input": {
                                      padding: "4px 5px !important",
                                      width: "100% !important",
                                    },
                                  },
                                },
                                openPickerButton: { sx: { color: "#67748e" } },
                              }}
                              reduceAnimations
                              disableHighlightToday
                            />
                          </LocalizationProvider>
                        </Box>

                        <Typography
                          sx={{
                            fontSize: "0.95rem",
                            fontWeight: 700,
                            color: "#0f1b2a",
                            mt: 2,
                            mb: 0.75,
                          }}
                        >
                          Ønsket tidspunkt
                        </Typography>
                        <Grid container spacing={1.5}>
                          {TIME_SLOTS.map((start) => {
                            const picked = field.time === start;
                            const disabled =
                              !field.date || [0, 6].includes(new Date(field.date).getDay());
                            return (
                              <Grid item xs={6} sm={3} key={start}>
                                <Box
                                  role="radio"
                                  aria-checked={picked}
                                  tabIndex={disabled ? -1 : 0}
                                  onClick={() =>
                                    !disabled && handleDateTimeChange(index, "time", start)
                                  }
                                  onKeyDown={(e) => {
                                    if (!disabled && (e.key === " " || e.key === "Enter")) {
                                      e.preventDefault();
                                      handleDateTimeChange(index, "time", start);
                                    }
                                  }}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    border: `1px solid ${picked ? "#1d7ff0" : "#dee0e2"}`,
                                    backgroundColor: picked ? "#eef5fe" : "#fff",
                                    borderRadius: "10px",
                                    px: 1,
                                    py: 1.25,
                                    cursor: disabled ? "not-allowed" : "pointer",
                                    opacity: disabled ? 0.5 : 1,
                                    "&:hover": {
                                      border: `1px solid ${disabled ? "#dee0e2" : "#1d7ff0"}`,
                                    },
                                  }}
                                >
                                  <Radio
                                    checked={picked}
                                    readOnly
                                    tabIndex={-1}
                                    size="small"
                                    sx={{
                                      p: 0.25,
                                      color: "#8a94a6",
                                      "&.Mui-checked": { color: "#1d7ff0" },
                                    }}
                                  />
                                  <Typography sx={{ fontSize: "0.95rem", color: "#0f1b2a" }}>
                                    {slotLabel(start)}
                                  </Typography>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                        {timeErrors[index] && (
                          <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                            {timeErrors[index]}
                          </Typography>
                        )}

                        {weatherData[index] && (
                          <Box mt={1.5}>
                            <WeatherConditionsCard
                              index={index}
                              date={
                                field.date
                                  ? `${field.date.split("-")[2]}.${field.date.split("-")[1]}.${
                                      field.date.split("-")[0]
                                    }`
                                  : ""
                              }
                              time={slotLabel(field.time)}
                              weatherForDate={weatherData[index]?.weather}
                            />
                          </Box>
                        )}
                      </Fragment>
                    ))}

                    {/* Flere alternativer er fortsatt mulig — kunden kan oppgi
                        flere tidspunkter, og produsenten velger ett av dem. */}
                    <SoftButton
                      size="small"
                      variant="text"
                      color="info"
                      onClick={handleAddDateTimeField}
                      sx={{ mt: 2, pl: 0 }}
                    >
                      <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
                      Legg til et alternativ
                    </SoftButton>

                    <Typography sx={{ color: "#67748e", fontSize: "0.8rem", mt: 1 }}>
                      Tidspunkt er estimert og vil avhenge av progresjon på tidligere leveranser.
                    </Typography>
                  </Box>
                )}
              </SoftBox>
              <Grid container justifyContent={"flex-end"}>
                <Grid item xs={12} sm={4} md={3} py={4}>
                  <SoftButton variant="gradient" color="info" fullWidth onClick={handleNextClick} disabled={isLoading}>
                    {isLoading ? <CircularProgress size={24} color="secondary" /> : "Neste"}
                  </SoftButton>
                </Grid>
              </Grid>
            </Box>
          )}
        </Grid>
      </Grid>
    </DefaultLayout>
  ) : (
    
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress size={60} color="info" />
    </Box>
  );
 
};

export default SearchAddress;
